/**
 * Manuscript index — chapter list with part filter, reading paths, and the
 * appendix expander. Stateful: tracks the active part filter, the active
 * reading path, and the appendix expander; the visual blocks live in
 * `manuscript-index-blocks.tsx` so this orchestrator stays focused on state.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppendixList,
  ChapterRows,
  PartBar,
  PartFilterChips,
  ReadingPaths,
} from "./manuscript-index-blocks.js";
import type {
  Manuscript,
  ManuscriptAppendix,
  ManuscriptChapter,
  ManuscriptItemKind,
  ManuscriptViewerTheme,
} from "./types.js";

interface ManuscriptIndexProps {
  readonly manuscript: Manuscript;
  readonly onOpen: (item: ManuscriptChapter | ManuscriptAppendix, kind: ManuscriptItemKind) => void;
  readonly theme: ManuscriptViewerTheme;
  readonly hidePartBar: boolean;
  readonly leadingContent?: React.ReactNode;
}

export function ManuscriptIndex({
  manuscript,
  onOpen,
  theme,
  hidePartBar,
  leadingContent,
}: ManuscriptIndexProps): React.JSX.Element {
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [filterPart, setFilterPart] = useState<string | null>(null);
  const [showAppendices, setShowAppendices] = useState<boolean>(false);

  // Reset filters whenever the manuscript identity changes — a reader who
  // switches from monograph to handbook should not inherit a stale filter
  // chosen against a different part vocabulary.
  useEffect(() => {
    setSelectedPath(null);
    setFilterPart(null);
    setShowAppendices(false);
  }, [manuscript.id]);

  const parts = manuscript.parts;
  const paths = manuscript.readingPaths;
  const appendices = manuscript.appendices ?? [];

  const totalWords = useMemo(
    () => manuscript.chapters.reduce((s, c) => s + (c.words ?? 0), 0),
    [manuscript],
  );
  const appWords = useMemo(() => appendices.reduce((s, a) => s + (a.words ?? 0), 0), [appendices]);

  const partTotals = useMemo(() => {
    if (parts === undefined) return {};
    const totals: Record<string, number> = {};
    for (const key of Object.keys(parts)) {
      totals[key] = manuscript.chapters
        .filter((c) => c.part === key)
        .reduce((s, c) => s + (c.words ?? 0), 0);
    }
    return totals;
  }, [manuscript, parts]);

  const highlighted = useMemo(() => {
    if (selectedPath === null || paths === undefined) return null;
    const path = paths[selectedPath];
    return path === undefined ? null : new Set(path.ids);
  }, [paths, selectedPath]);

  const pathWords = useCallback(
    (pathKey: string): number => {
      if (paths === undefined) return 0;
      const path = paths[pathKey];
      if (path === undefined) return 0;
      const id = new Set(path.ids);
      return manuscript.chapters
        .filter((c) => id.has(c.id))
        .reduce((s, c) => s + (c.words ?? 0), 0);
    },
    [manuscript, paths],
  );

  const showPartBar = !hidePartBar && parts !== undefined && Object.keys(parts).length > 0;

  return (
    <div style={{ padding: "20px 32px", display: "grid", gap: 16 }}>
      {leadingContent}

      {showPartBar ? (
        <PartBar
          parts={parts}
          partTotals={partTotals}
          totalWords={totalWords}
          filterPart={filterPart}
          setFilterPart={setFilterPart}
          theme={theme}
        />
      ) : null}

      {paths !== undefined ? (
        <ReadingPaths
          paths={paths}
          selectedPath={selectedPath}
          setSelectedPath={setSelectedPath}
          pathWords={pathWords}
          theme={theme}
        />
      ) : null}

      {parts !== undefined ? (
        <PartFilterChips
          parts={parts}
          filterPart={filterPart}
          setFilterPart={setFilterPart}
          theme={theme}
        />
      ) : null}

      <ChapterRows
        chapters={manuscript.chapters}
        filterPart={filterPart}
        highlighted={highlighted}
        parts={parts}
        theme={theme}
        onOpen={onOpen}
      />

      {appendices.length > 0 ? (
        <AppendixList
          appendices={appendices}
          appWords={appWords}
          showAppendices={showAppendices}
          setShowAppendices={setShowAppendices}
          theme={theme}
          onOpen={onOpen}
        />
      ) : null}
    </div>
  );
}
